import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  EuiLoadingSpinner,
  EuiPanel,
  EuiText,
  EuiSelect,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFieldText,
  EuiFormRow,
  EuiTextArea,
  EuiComboBox,
  EuiDatePicker,
  EuiForm,
  EuiFieldNumber 
} from '@elastic/eui';
import '../style.css';
import dayjs from "dayjs";
import PlaylistModal from "../components/PlaylistModal";
import {TranslateLanguage} from './KeywordTranslator';
import ScriptureExcelUpdater from './ScriptureExcelUpdater';
import YouTubeSongImporter from './YouTubeSongImporter';
import { getYouTubeVideoId } from './YouTubeSermonImporter';

const normalizeTags = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value || '').split(/[,，;；]/).map((item) => item.trim()).filter(Boolean);
};

const SongManagement = () => {
  const [songs, setSongs] = useState([]); //所有獲取的詩歌
  const [invalidsongs, setInvalidSongs] = useState([]); //檢查失效的詩歌
  const [checkedsongs, setCheckedSongs] = useState([]); //檢查完成的詩歌
  const [showsongs, setShowSongs] = useState([]); //列表顯示的詩歌
  const [loading, setLoading] = useState(true);
  const [selectedSong, setSelectedSong] = useState(null);
  const [sortKey, setSortKey] = useState('title'); // 默认按标题排序
  const [languageOptions, setLanguageOptions] = useState([]);
  const [tempoOptions, setTempoOptions] = useState([]);
  const [officialOptions, setOfficialOptions] = useState([]);
  const [resultMessage, setResultMessage] = useState(''); // 用于显示提示信息
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isNewSong, setIsNewSong] = useState(false);  // 用于判断是否是新增诗歌
  const [searchTerm, setSearchTerm] = useState(''); // 用于存储搜索关键词
  const [totalCount, setTotalCount] = useState(0);
  const [showPlaylistModal, setShowModal] = useState(false);
  const [showYouTubeImporter, setShowYouTubeImporter] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [isChecking, setIsChecking] = useState(false);
  const [isFetchingYouTubeMetadata, setIsFetchingYouTubeMetadata] = useState(false);
  const [showFilterOptions, setShowFilterOptions] = useState(false); // 是否显示过滤选项
  const [startIndex, setStartIndex] = useState(1); // 
  const [endIndex, setEndIndex] = useState(2); // 
  const [checkedNum, setCheckedNum] = useState(2); // 
  const [modalProps, setModalProps] = useState({
    userId: '',
    songId: '',
    videoURL: '',
    type: '',
    engineId: '',
    title: '',
    label: '',
    playlisttype: ''
  });

  // —— 新增：一个安全解析时间的工具 —— //
  const getTimeMs = (val) => {
    if (!val) return -1; // 让空值排最后
    const t = dayjs(val);
    return t.isValid() ? t.valueOf() : -1;
  };

  const fetchAllSongs = async (signal) => {
    const apiURL = process.env.REACT_APP_MasterApiURL;
    const apiKey = process.env.REACT_APP_APIKey;
    const pageSize = 1000;
    let currentPage = 1;
    let hasMoreData = true;

    let fetchedSongs = [];

    try {
      while (hasMoreData) {
        const response = await fetch(apiURL, {
          method: "POST",
          signal,
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer search-" + apiKey
          },
          body: JSON.stringify({
            query: "",
            filters: { "all": [{ "doctype": "詩歌" }] },
            page: { size: pageSize, current: currentPage },
            sort: { id: "asc" }
          })
        });

        if (response.ok) {
          const data = await response.json();
          const songs = data.results.map(song => {
            let length = song.length.raw || "";
          
            // 检查 length 是否为小数格式，并进行转换
            if (!isNaN(parseFloat(length)) && isFinite(length)) {
              length = convertDecimalToTime(parseFloat(length));  // 将小数转换为 hh:mm:ss
            }
            // console.log("Process Song:", song);
            return {
              id: song.id.raw || "",
              title: song.title.raw || "",
              link: song.link.raw || "",
              doctype: song.doctype.raw || "",
              publisher: song.publisher.raw || "",
              description: song.description.raw || "",
              language: song.language.raw || "",
              length,  // 将转换后的或原始的 length 值赋给对象
              tempo: song.tempo.raw || "",
              key: song.key.raw || "",
              tag: song.tag.raw || "",
              official: song.official.raw || "",
              verselist: song.verselist.raw || "",
              viewct: song.viewct?.raw || "",
              date: song.date?.raw || ""
              // viewct: song.viewct.raw || "",
              // date: song.date.raw || ""
            };
          });

          fetchedSongs = [...fetchedSongs, ...songs];

          if (songs.length < pageSize) {
            hasMoreData = false;
          } else {
            currentPage++;
          }
        } else {
          console.error("Error fetching songs: ", response.statusText);
          return;
        }
      }

      if (signal.aborted) return;
      setSongs(fetchedSongs);
      setShowSongs(fetchedSongs);
      console.log("All songs fetched:", fetchedSongs);
      setTotalCount(fetchedSongs.length);  // 更新总条数
      setStartIndex(1);
      setEndIndex(fetchedSongs.length);
      // 从所有诗歌中提取唯一的 Language、Tempo 和 Official 选项
      const uniqueLanguages = Array.from(new Set(fetchedSongs.map(song => song.language))).filter(Boolean);
      const uniqueTempos = Array.from(new Set(fetchedSongs.map(song => song.tempo))).filter(Boolean);
      const uniqueOfficials = Array.from(new Set(fetchedSongs.map(song => song.official))).filter(Boolean);

      // 设置下拉菜单的选项
      setLanguageOptions(uniqueLanguages.map(lang => ({ label: lang })));
      setTempoOptions(uniqueTempos.map(tempo => ({ label: tempo })));
      setOfficialOptions(uniqueOfficials.map(official => ({ label: official })));
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error("Error while fetching songs: ", error);
    }
    if (!signal.aborted) setLoading(false);
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchAllSongs(controller.signal);
    return () => controller.abort();
  }, []);
  
  // 提取ID中的数字部分
  const extractIdNumber = (id) => {
    const match = id.match(/\d+$/);  // 匹配最后的数字部分
    return match ? parseInt(match[0], 10) : id;
  };
  const getAvailableIds = (count = 1) => {
    const usedIds = new Set(
      songs.map((song) => extractIdNumber(String(song.id))).filter(Number.isFinite)
    );
    const availableIds = [];
    let candidate = 1;
    while (availableIds.length < count) {
      if (!usedIds.has(candidate)) availableIds.push(candidate);
      candidate += 1;
    }
    return availableIds;
  };

  const getNextId = () => getAvailableIds(1)[0];

  // 根据搜索关键词过滤列表
  const filteredSongs = showsongs.filter((song) =>
    song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    song.id.toLowerCase().includes(searchTerm.toLowerCase()) // 检查 ID 是否包含关键词
  );

  // 按位数排序
  const sortSongs = (songs, key) => {
    if (key === 'id') {
      return [...songs].sort((a, b) => {
        const idA = extractIdNumber(a.id);
        const idB = extractIdNumber(b.id);
        return idA - idB;
      });
    }
    if (key === 'dateDesc' || key === 'dateAsc') {
      return [...songs].sort((a, b) => {
        const ta = getTimeMs(a.date);
        const tb = getTimeMs(b.date);
        // 空日期放到最后
        if (ta === -1 && tb === -1) return 0;
        if (ta === -1) return 1;
        if (tb === -1) return -1;
        return key === 'dateDesc' ? tb - ta : ta - tb;
      });
    }
    // 如果是按 title 排序，统一转换为小写字母进行比较
    return [...songs].sort((a, b) => {
      const valueA = (a[key]?.trim()?.toLowerCase() || "").normalize("NFC"); // 去除空格并规范字符编码
      const valueB = (b[key]?.trim()?.toLowerCase() || "").normalize("NFC");
      return valueA.localeCompare(valueB, "zh-Hans"); // 按中文规则排序
    });
  };

  // 对过滤后的结果进行排序
  const sortedSongs = sortSongs(filteredSongs, sortKey);
  //console.log(sortedSongs); // 调试输出，检查排序结果

  // 计算时长，将 hh:mm:ss 或 mm:ss 转换为小数（小时数）
    function calculateLength(timeString) {
        const parts = timeString.split(':').map(Number);
        let hours = 0;
        let minutes = 0;
        let seconds = 0;

        // 根据输入的长度判断是 mm:ss 还是 hh:mm:ss
        if (parts.length === 2) {
            // 如果输入格式为 mm:ss
            minutes = parts[0];
            seconds = parts[1];
        } else if (parts.length === 3) {
            // 如果输入格式为 hh:mm:ss
            hours = parts[0];
            minutes = parts[1];
            seconds = parts[2];
        }

        // 转换为小时
        const totalHours = hours + (minutes / 60) + (seconds / 3600);
        return totalHours * 2.5;  // 根据您提供的比例进行转换
    }

    // 将小数转换为 hh:mm:ss 格式
    function convertDecimalToTime(decimal) {
        const totalSeconds = Math.round(decimal * 3600 / 2.5); // 将小数转换为总秒数
        const hours = Math.floor(totalSeconds / 3600);          // 计算小时
        const remainingSeconds = totalSeconds % 3600;
        const minutes = Math.floor(remainingSeconds / 60);      // 计算分钟
        const seconds = remainingSeconds % 60;                  // 计算秒数

        // 如果小时为 0，只显示 mm:ss 格式
        if (hours === 0) {
            return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
        }

        // 否则显示完整的 hh:mm:ss 格式
        return `${hours}:${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    }

    // 解析时间字符串 hh:mm:ss 或 mm:ss
    function parseTimeString(timeString) {
        const parts = timeString.split(':').map(Number);

        if (parts.length === 2) {
            // 处理 mm:ss 格式
            return {
                hours: 0,
                minutes: parts[0],
                seconds: parts[1],
            };
        } else if (parts.length === 3) {
            // 处理 hh:mm:ss 格式
            return {
                hours: parts[0],
                minutes: parts[1],
                seconds: parts[2],
            };
        } else {
            throw new Error("Invalid time format");
        }
    }

  const handleSongSelect = (song) => {
      setSelectedSong(song); // 设置选中的歌曲
      setIsNewSong(false);  // 保存成功后，取消新增标识
      setResultMessage(''); // 清空 resultMessage
    };
  

  const handleSongCheck = async () => {
    if (startIndex < 1 || endIndex > songs.length || startIndex > endIndex) {
      window.alert('請輸入有效的起始和終止編號。');
      return;
    }
    const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY || localStorage.getItem('ctb.youtubeDataApiKey') || '';
    if (!apiKey) {
      window.alert('尚未設定 YouTube 讀取服務，請在前端環境變數中設定 REACT_APP_YOUTUBE_API_KEY。');
      return;
    }
    const sorted = sortSongs(songs, 'id');
    const rangeSongs = sorted.slice(startIndex - 1, endIndex);
    const mappings = rangeSongs.map((song) => ({ song, videoId: getYouTubeVideoId(song.link) }));
    const videoIds = [...new Set(mappings.map((item) => item.videoId).filter(Boolean))];
    const totalBatches = Math.max(1, Math.ceil(videoIds.length / 50));
    const availableVideos = new Map();
    const startTime = Date.now();
    setShowFilterOptions(false);
    setIsChecking(true);
    setProgress({ completed: 0, total: totalBatches, elapsedTime: 0 });
    try {
      for (let index = 0; index < videoIds.length; index += 50) {
        const batch = videoIds.slice(index, index + 50);
        const query = new URLSearchParams({
          part: 'id,status',
          id: batch.join(','),
          key: apiKey,
        });
        const response = await fetch(`https://www.googleapis.com/youtube/v3/videos?${query}`);
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error?.message || `YouTube API 檢查失敗 (${response.status})`);
        }
        (data.items || []).forEach((video) => {
          let reason = '';
          if (!['public', 'unlisted'].includes(video.status?.privacyStatus)) {
            reason = '影片不是公開或不公開連結';
          } else if (video.status?.uploadStatus !== 'processed') {
            reason = `影片尚未處理完成（${video.status?.uploadStatus || '狀態不明'}）`;
          } else if (video.status?.embeddable === false) {
            reason = '影片擁有者禁止站內嵌入播放';
          }
          availableVideos.set(video.id, { isValid: !reason, reason });
        });
        setProgress({
          completed: Math.floor(index / 50) + 1,
          total: totalBatches,
          elapsedTime: Math.floor((Date.now() - startTime) / 1000),
        });
      }
      if (!videoIds.length) {
        setProgress({ completed: 1, total: 1, elapsedTime: 0 });
      }
      const resultById = new Map(mappings.map(({ song, videoId }) => {
        if (!videoId) {
          return [song.id, { isValid: false, reason: '不是可辨識的 YouTube 影片網址' }];
        }
        return [song.id, availableVideos.get(videoId) || {
          isValid: false,
          reason: '影片已刪除、設為私人或不存在',
        }];
      }));
      const checkedSongsResult = rangeSongs.map((song) => {
        const result = resultById.get(song.id);
        return { ...song, isValid: result.isValid, invalidReason: result.reason };
      });
      const invalidSongs = checkedSongsResult.filter((song) => !song.isValid);
      const updatedSongs = songs.map((song) => (
        resultById.has(song.id)
          ? { ...song, isValid: resultById.get(song.id).isValid, invalidReason: resultById.get(song.id).reason }
          : song
      ));
      setSongs(updatedSongs);
      setCheckedSongs(checkedSongsResult);
      setInvalidSongs(invalidSongs);
      setCheckedNum(rangeSongs.length);
      setTotalCount(invalidSongs.length ? invalidSongs.length : updatedSongs.length);
      setShowSongs(invalidSongs.length ? invalidSongs : updatedSongs);
      setShowFilterOptions(true);
    } catch (error) {
      console.error('YouTube API 詩歌檢查失敗:', error);
      window.alert(error.message || 'YouTube API 詩歌檢查失敗，請稍後再試。');
    } finally {
      setIsChecking(false);
    }
  };

  // 点击"新建诗歌"按钮时调用
  const handleNewSong = () => {
    const newSong = {
      id: getNextId().toString(),  // ID为最大值+1
      title: '',
      link: '',
      publisher: '',
      description: '',
      language: '',
      length: '',
      tempo: '中',
      key: '',
      tag: '',
      official: '',
      verselist: '',
      viewct: '',
      date: '',
    };
    setSelectedSong(newSong);  // 将新诗歌设置为选中的诗歌
    setIsNewSong(true);  // 设置为新增模式
    setResultMessage('');  // 清空结果提示
  };

  const formatYouTubeDuration = (iso = '') => {
    const match = iso.match(/^P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/);
    if (!match) return '';
    const totalSeconds = (Number(match[1] || 0) * 86400) + (Number(match[2] || 0) * 3600)
      + (Number(match[3] || 0) * 60) + Number(match[4] || 0);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return hours
      ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      : `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  const fillSongFromYouTube = async () => {
    if (!isNewSong) {
      setResultMessage('已有詩歌不會自動覆蓋資料；請使用「更新觀看次數」。');
      return;
    }
    const videoId = getYouTubeVideoId(selectedSong?.link);
    if (!videoId) {
      setResultMessage('請先輸入有效的 YouTube 影片連結。');
      return;
    }
    const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY || localStorage.getItem('ctb.youtubeDataApiKey') || '';
    if (!apiKey) {
      setResultMessage('尚未設定 YouTube 讀取服務，請在前端環境變數中設定 REACT_APP_YOUTUBE_API_KEY。');
      return;
    }
    setIsFetchingYouTubeMetadata(true);
    setResultMessage('正在讀取 YouTube 影片資料…');
    try {
      const query = new URLSearchParams({ part: 'snippet,contentDetails,statistics', id: videoId, key: apiKey });
      const response = await fetch(`https://www.googleapis.com/youtube/v3/videos?${query}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error?.message || `YouTube API 請求失敗 (${response.status})`);
      const video = data.items?.[0];
      if (!video) throw new Error('找不到這部影片，或影片不是公開的。');
      const snippet = video.snippet || {};
      const hashtags = (snippet.description || '').match(/#[^\s#]+/g) || [];
      setSelectedSong(current => ({
        ...current,
        title: snippet.title || current.title,
        publisher: snippet.channelTitle || current.publisher,
        description: snippet.description || current.description,
        language: snippet.defaultAudioLanguage || snippet.defaultLanguage || current.language,
        length: formatYouTubeDuration(video.contentDetails?.duration) || current.length,
        tag: hashtags.length ? hashtags.map(tag => tag.slice(1)).join(', ') : current.tag,
        viewct: video.statistics?.viewCount || current.viewct,
        date: String(snippet.publishedAt || '').slice(0, 10) || current.date,
      }));
      setResultMessage('已讀取 YouTube 資料，請檢查語言、節奏和其他欄位後保存。');
    } catch (error) {
      setResultMessage(error.message || '讀取 YouTube 影片資料失敗。');
    } finally {
      setIsFetchingYouTubeMetadata(false);
    }
  };

  const updateYouTubeViewCount = async () => {
    const videoId = getYouTubeVideoId(selectedSong?.link);
    if (!videoId) {
      setResultMessage('請先輸入有效的 YouTube 影片連結。');
      return;
    }
    const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY || localStorage.getItem('ctb.youtubeDataApiKey') || '';
    if (!apiKey) {
      setResultMessage('尚未設定 YouTube 讀取服務，請在前端環境變數中設定 REACT_APP_YOUTUBE_API_KEY。');
      return;
    }
    setIsFetchingYouTubeMetadata(true);
    setResultMessage('正在更新 YouTube 觀看次數…');
    try {
      const query = new URLSearchParams({ part: 'statistics', id: videoId, key: apiKey });
      const response = await fetch(`https://www.googleapis.com/youtube/v3/videos?${query}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error?.message || `YouTube API 請求失敗 (${response.status})`);
      const video = data.items?.[0];
      if (!video) throw new Error('找不到這部影片，或影片不是公開的。');
      const viewCount = video.statistics?.viewCount;
      if (viewCount === undefined) throw new Error('YouTube 沒有返回這部影片的觀看次數。');
      setSelectedSong(current => ({ ...current, viewct: viewCount }));
      setResultMessage(`觀看次數已更新為 ${Number(viewCount).toLocaleString()}，請點擊「保存修改」寫入資料。`);
    } catch (error) {
      setResultMessage(error.message || '更新 YouTube 觀看次數失敗。');
    } finally {
      setIsFetchingYouTubeMetadata(false);
    }
  };

  async function handleDeleteSongClick() {
    if (window.confirm(TranslateLanguage(localStorage.userLanguage, "確定刪除這首詩歌？"))) {
      const postURL = process.env.REACT_APP_SongDocURL; // API 的 URL
      const postKey = process.env.REACT_APP_PostKey;   // 授权密钥

      // 检查是否选择了歌曲
      if (!selectedSong || !selectedSong.id) {
          console.error("No song selected for deletion.");
          return;
      }

      const songId = extractIdNumber(selectedSong.id);
          // 将 songId 转为字符串并生成 JSON
      const bodyContent = JSON.stringify([String(songId)]);

      try {
          const response = await fetch(postURL, {
              method: "DELETE",
              headers: {
                  "Content-Type": "application/json",
                  "Authorization": postKey,
              },
              body: bodyContent, // 删除请求的 JSON 数据
          });

          if (response.ok) {
              const result = await response.json();
              console.log("Song Deleted Successfully:", result);
          } else {
              console.error(
                  `Error deleting song: ${response.status} ${response.statusText}`
              );
              const errorDetails = await response.text();
              console.error("Error details:", errorDetails);
          }
      } catch (error) {
          console.error("Error while deleting song:", error.message);
      }
    }
}

  async function handleDeleteInvalidSong(song, event) {
    event?.stopPropagation();
    if (song?.isValid !== false && !song?.invalidReason) return;
    if (!window.confirm(`確定永久刪除失效詩歌「${song.title}」？刪除後該 ID 可供新項目再次使用。`)) return;

    const songId = extractIdNumber(String(song.id));
    try {
      await axios.delete('/api/admin/search-documents/song', {
        headers: {
          'X-Elastic-Authorization': process.env.REACT_APP_PostKey,
        },
        data: { documentIds: [String(songId)] },
      });

      setSongs((current) => current.filter((item) => item.id !== song.id));
      setInvalidSongs((current) => current.filter((item) => item.id !== song.id));
      setCheckedSongs((current) => current.filter((item) => item.id !== song.id));
      setShowSongs((current) => {
        const next = current.filter((item) => item.id !== song.id);
        setTotalCount(next.length);
        return next;
      });
      setEndIndex((current) => Math.max(1, Math.min(current, songs.length - 1)));
      if (selectedSong?.id === song.id) setSelectedSong(null);
      setResultMessage(`已刪除失效詩歌，ID ${songId} 現可再次使用。`);
    } catch (error) {
      console.error('刪除失效詩歌失敗:', error);
      window.alert(error?.response?.data?.message || error.message || '刪除失效詩歌失敗，請稍後再試。');
    }
  }

  
  // 保存修改的函数
  async function handleSaveSong() {
      // 设置初始提示信息
      setResultMessage("Start updating song...");

        // 验证红框内的项目是否为空
      if (!selectedSong.title || !selectedSong.link || !selectedSong.length || !selectedSong.language || !selectedSong.tempo || !selectedSong.publisher) {
        setResultMessage('Error: All fields must be filled.');
        return;  // 阻止保存操作
      }

      // 验证 link 是否为有效 URL
      const urlPattern = new RegExp('^(https?:\\/\\/)?' + // 协议
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|(\\d{1,3}\\.){3}\\d{1,3})' + // 域名或IP
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // 端口和路径
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // 查询字符串
        '(\\#[-a-z\\d_]*)?$', 'i'); // 片段标识符

      if (!urlPattern.test(selectedSong.link)) {
        setResultMessage('Error: The Song Link must be a valid URL.');
        return;  // 阻止保存操作
      }
        // 如果是新增的诗歌（通过 isNewSong 判断），检查 Song Link 是否已存在
      if (isNewSong) {
        const linkExists = songs.some(song => song.link === selectedSong.link);
        if (linkExists) {
          setResultMessage('Error: The Song Link already exists. Please enter a unique link.');
          return;  // 阻止保存操作
        }
      }
      // 获取表单输入值（使用 EUI 组件的值获取方法）
      const title = selectedSong.title;
      const link = selectedSong.link;
      const songId = extractIdNumber(selectedSong.id);

      // 获取 EuiComboBox 选中的值 (假设是单选)
      const language = selectedSong.language;
      const tempo = selectedSong.tempo;
      const official = selectedSong.official;
      // 解析 length (时间) 字段
      let length;
      try {
          const timeString = selectedSong.length;  // 获取时间字符串
          const { hours, minutes, seconds } = parseTimeString(timeString);    // 使用 parseTimeString 解析时间
          length = calculateLength(`${hours}:${minutes}:${seconds}`).toFixed(6); // 计算并转换为小数
      } catch (error) {
          setResultMessage("Invalid time format. Please enter a valid time (mm:ss or hh:mm:ss).");
          return;  // 时间格式无效，直接返回
      }

      // 构建新的歌曲对象
      const newSong = {
          id: songId,
          doctype: "詩歌",
          title: title,
          link: link,
          publisher: selectedSong.publisher,
          description: selectedSong.description,
          language: language,  // 从 EuiComboBox 获取的值
          length: selectedSong.length,  // 时间字符串
          tempo: tempo,  // 从 EuiComboBox 获取的值
          key: selectedSong.key,
          tag: normalizeTags(selectedSong.tag),
          official: official,  // 从 EuiComboBox 获取的值
          verselist: selectedSong.verselist,
          viewct: selectedSong.viewct === '' ? null : selectedSong.viewct, // 如果为空字符串，设为 null
          date: selectedSong.date,
          // versecontent: document.getElementById('versecontent').value
      };

      // 调用上传函数并处理响应
      const uploadSuccess = await uploadSong(newSong);

      // 更新提示信息
      if (uploadSuccess) {
          newSong.id = "ctb-worship-tc|"+songId;
          newSong.length = selectedSong.length;
          setSelectedSong(newSong);  // 使用返回的最新数据更新当前选中歌曲
          setResultMessage("Song updated successfully!");
          setSongs((prevSongs) => {
            const songExists = prevSongs.some(song => song.id === newSong.id);
            // if (!songExists) {
            //   return [...prevSongs, newSong];  // 如果不存在，添加新诗歌
            // } else {
            //   // 如果已存在，替换更新后的歌曲
            //   return prevSongs.map(song => 
            //     song.id === newSong.id ? newSong : song
            //   );
            // }
            const updatedSongs = songExists
            ? prevSongs.map(song => song.id === newSong.id ? newSong : song)
            : [...prevSongs, newSong]; // 如果存在，替换；如果不存在，添加
        
            // 同步更新 setShowSongs
            setShowSongs(updatedSongs);
            return updatedSongs;
          });
          if (isNewSong){
            setTotalCount(totalCount+1);  // 更新总条数
          }
          setIsNewSong(false);  // 保存成功后，取消新增标识
      } else {
          setResultMessage("Song update failed.");
      }
  };

  async function uploadSong(song) {
    const postURL = process.env.REACT_APP_SongDocURL;
    const postKey = process.env.REACT_APP_PostKey;
      try {
          const response = await fetch(postURL, {
              method: "POST",
              headers: {
                  "Content-Type": "application/json",
                  "Authorization": postKey
              },
              body: JSON.stringify([song])  // 上传时将歌曲对象封装为数组
          });

          if (response.ok) {
              console.log("Song uploaded: ", await response.json());
              return true;  // 上传成功
          } else {
              console.error("Error uploading song: ", response.statusText);
              return false;  // 上传失败
          }
      } catch (error) {
          console.error("Error while uploading song: ", error);
          return false;  // 处理异常
      }
  }

  async function handleYouTubeImport(importedVideos) {
    const knownVideoIds = new Set(songs.map((song) => getYouTubeVideoId(song.link)).filter(Boolean));
    const uniqueVideos = importedVideos.filter((video) => {
      const videoId = getYouTubeVideoId(video.link);
      if (!videoId || knownVideoIds.has(videoId)) return false;
      knownVideoIds.add(videoId);
      return true;
    });
    if (!uniqueVideos.length) throw new Error('選擇的影片已經全部存在。');

    const availableIds = getAvailableIds(uniqueVideos.length);
    const documents = uniqueVideos.map((video, index) => ({
      ...video,
      id: availableIds[index],
      doctype: '詩歌',
      tag: normalizeTags(video.tag),
    }));
    const response = await fetch(process.env.REACT_APP_SongDocURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_PostKey,
      },
      body: JSON.stringify(documents),
    });
    if (!response.ok) throw new Error(`Elastic 批次寫入失敗 (${response.status})。`);

    const result = await response.json();
    if (Array.isArray(result) && result.some((item) => item?.errors?.length)) {
      console.error('YouTube song import document errors:', result);
      throw new Error('部分詩歌未能寫入 Elastic，請查看瀏覽器記錄。');
    }

    const addedSongs = documents.map((document) => ({ ...document, id: `ctb-worship-tc|${document.id}` }));
    setSongs((current) => [...current, ...addedSongs]);
    setShowSongs((current) => [...current, ...addedSongs]);
    setTotalCount((current) => current + addedSongs.length);
    setEndIndex((current) => current + addedSongs.length);
    return uniqueVideos.map((video) => getYouTubeVideoId(video.link));
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <EuiLoadingSpinner size="xl" />
        <EuiText color="default" style={{ marginTop: '10px' }}>
          正在加载中...
        </EuiText>
      </div>
    );
  }

  // 新增功能：将所有诗歌保存为 JSON 文件
  const saveAsJson = (songsList = songs) => {
    // 按照 id 进行排序
    const sortedSongs = [...songsList].sort((a, b) => {
      const idA = parseInt(a.id.match(/\d+/), 10);
      const idB = parseInt(b.id.match(/\d+/), 10);
      return idA - idB;
    });

    // 添加总数量，并列出所有字段
    const jsonContent = {
      total: sortedSongs.length,
      songs: sortedSongs.map(song => ({
        id: song.id,
        doctype: song.doctype,
        title: song.title,
        link: song.link,
        publisher: song.publisher,
        description: song.description,
        language: song.language,
        length: song.length,
        tempo: song.tempo,
        key: song.key,
        tag: song.tag,
        official: song.official,
        verselist: song.verselist,
        viewct: song.viewct,
        date: song.date,
        invalidReason: song.invalidReason || ''
      }))
    };

    const blob = new Blob([JSON.stringify(jsonContent, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    const now = new Date();
    const formattedDate = now.toISOString().slice(0, 10); // 获取当前日期 (YYYY-MM-DD)
    const formattedTime = now.toISOString().slice(11, 19).replace(/:/g, "-"); // 获取当前时间 (HH-MM-SS)，并将冒号替换为 "-"
    link.href = URL.createObjectURL(blob);
    link.download = `songs_${formattedDate}_${formattedTime}.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 0);
  };


  const saveAsExcel = async (songsList = songs) => {
    if (isExportingExcel) return;

    setIsExportingExcel(true);
    try {
      const sortedSongs = [...songsList].sort((a, b) => {
        const idA = parseInt(String(a.id).match(/\d+/)?.[0] || '0', 10);
        const idB = parseInt(String(b.id).match(/\d+/)?.[0] || '0', 10);
        return idA - idB;
      });

      const language = localStorage.userLanguage || 'T';
      const translate = text => TranslateLanguage(language, text);
      const ExcelJSModule = await import('exceljs');
      const ExcelJS = ExcelJSModule.default || ExcelJSModule;
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'CheckTheBible';
      workbook.created = new Date();
      workbook.modified = new Date();

      const worksheet = workbook.addWorksheet(translate('詩歌'), {
        views: [{ state: 'frozen', ySplit: 1 }],
        pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
      });

      const columnDefinitions = [
        { header: 'ID', key: 'id', width: 14 },
        { header: translate('類型'), key: 'doctype', width: 10 },
        { header: translate('標題'), key: 'title', width: 32 },
        { header: translate('連結'), key: 'link', width: 45 },
        { header: translate('發佈者'), key: 'publisher', width: 24 },
        { header: translate('描述'), key: 'description', width: 50 },
        { header: translate('語言'), key: 'language', width: 12 },
        { header: translate('時長'), key: 'length', width: 12 },
        { header: translate('速度'), key: 'tempo', width: 10 },
        { header: translate('調性'), key: 'key', width: 10 },
        { header: translate('標籤'), key: 'tag', width: 24 },
        { header: translate('官方'), key: 'official', width: 10 },
        { header: translate('經文'), key: 'verselist', width: 30 },
        { header: translate('觀看次數'), key: 'viewct', width: 14 },
        { header: translate('日期'), key: 'date', width: 14 },
        { header: translate('失效原因'), key: 'invalidReason', width: 34 },
      ];

      worksheet.columns = columnDefinitions.map(({ header, key, width }) => ({ header, key, width }));
      worksheet.autoFilter = { from: 'A1', to: 'P1' };

      sortedSongs.forEach(song => {
        const rawViewCount = String(song.viewct ?? '').replace(/,/g, '').trim();
        const numericViewCount = rawViewCount !== '' && /^\d+(?:\.\d+)?$/.test(rawViewCount)
          ? Number(rawViewCount)
          : (song.viewct ?? '');
        const dateMatch = String(song.date ?? '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
        const excelDate = dateMatch
          ? new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]))
          : (song.date ?? '');

        const row = worksheet.addRow({
          id: song.id ?? '',
          doctype: song.doctype ?? '',
          title: song.title ?? '',
          link: song.link
            ? { text: song.link, hyperlink: song.link, tooltip: song.link }
            : '',
          publisher: song.publisher ?? '',
          description: song.description ?? '',
          language: song.language ?? '',
          length: song.length ?? '',
          tempo: song.tempo ?? '',
          key: song.key ?? '',
          tag: song.tag ?? '',
          official: song.official ?? '',
          verselist: song.verselist ?? '',
          viewct: numericViewCount,
          date: excelDate,
          invalidReason: song.invalidReason ?? '',
        });

        row.alignment = { vertical: 'top' };
        row.getCell('description').alignment = { vertical: 'top', wrapText: true };
        row.getCell('verselist').alignment = { vertical: 'top', wrapText: true };
        row.getCell('invalidReason').alignment = { vertical: 'top', wrapText: true };
        if (song.link) {
          row.getCell('link').font = { color: { argb: 'FF0563C1' }, underline: true };
        }
        if (dateMatch) {
          row.getCell('date').numFmt = 'yyyy-mm-dd';
        }
        if (typeof numericViewCount === 'number') {
          row.getCell('viewct').numFmt = '#,##0';
        }
      });

      const headerRow = worksheet.getRow(1);
      headerRow.height = 24;
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E78' } };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      const now = new Date();
      const formattedDate = now.toISOString().slice(0, 10);
      const formattedTime = now.toISOString().slice(11, 19).replace(/:/g, '-');
      downloadLink.href = downloadUrl;
      downloadLink.download = `songs_${formattedDate}_${formattedTime}.xlsx`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
    } catch (error) {
      console.error('Excel export failed:', error);
      window.alert(TranslateLanguage(localStorage.userLanguage, 'Excel 匯出失敗，請稍後再試。'));
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleAddplaylistClick = () => {
    const user = sessionStorage.getItem('user');
    if (user) {
      // 如果用户已登录，显示 PlaylistModal 组件
      setModalProps({
        userId: JSON.parse(user).id, // 假设 user 对象里有 id 字段
        songId: selectedSong.id,
        videoURL: selectedSong.link,
        type: selectedSong.doctype, 
        engineId: selectedSong.id, 
        title: selectedSong.title,
        label: 'v',
        playlisttype: 'radio'
      });
      setShowModal(true);
    } else {
      return (
        <EuiText color="danger">
          请登录管理员用户操作。
        </EuiText>
      );
    }
  };

  const handleScriptureSongsUpdated = (updatedScriptures) => {
    const applyUpdates = (song) => updatedScriptures.has(String(song.id))
      ? { ...song, verselist: updatedScriptures.get(String(song.id)) }
      : song;

    setSongs(previousSongs => previousSongs.map(applyUpdates));
    setShowSongs(previousSongs => previousSongs.map(applyUpdates));
    setSelectedSong(previousSong => previousSong ? applyUpdates(previousSong) : previousSong);
  };
  return (
    <EuiFlexGroup
      className="layout-responsive"         // ⭐ 关键：用于媒体查询控制布局
      gutterSize="m"
      wrap                                  // 允许在小屏/竖屏自动换行
      responsive
    >
      {/* 左侧歌曲列表 */}
      <EuiFlexItem grow={false} className="song-list-container">
        <EuiPanel paddingSize="s">
          <EuiFormRow label={`诗歌列表 共${totalCount}條`}>
            <EuiSelect
              options={[
                { value: 'title', text: '按标题排序' },
                { value: 'id', text: '按ID排序' },
                { value: 'dateDesc', text: '按发布日期（新→旧）' },   // ⭐ 新增
                { value: 'dateAsc', text: '按发布日期（旧→新）' }    // ⭐ 新增
              ]}
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
            />
          </EuiFormRow>

          {/* 搜索框 */}
          <input
            type="text"
            placeholder="搜索诗歌..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"            // ⭐ 方便在 CSS 中统一样式
            // style={{ marginBottom: '10px', padding: '5px', width: '100%' }}
          />

          <ul className="song-list">
            {/* {sortSongs(songs, sortKey).map((song) => ( */}
            {sortedSongs.map((song) => (
              <li key={song.id} onClick={() => handleSongSelect(song)}>
                <div>{song.title} (ID: {song.id})</div>
                {song.invalidReason && (
                  <div style={{ color: '#bd271e', fontSize: '12px', marginTop: '3px' }}>
                    <div>失效原因：{song.invalidReason}</div>
                    <EuiButton
                      size="s"
                      color="danger"
                      iconType="trash"
                      style={{ marginTop: '6px' }}
                      onClick={(event) => handleDeleteInvalidSong(song, event)}
                    >
                      刪除失效詩歌
                    </EuiButton>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </EuiPanel>
      </EuiFlexItem>

      {/* 右侧详细信息编辑 */}
      <EuiFlexItem className="detail-pane">
        <EuiFlexGroup direction="column">
        {selectedSong ? (
          <EuiPanel paddingSize="l">
            <h3>{isNewSong ? '新建詩歌' : `編輯詩歌：${selectedSong.title}`}</h3>

            <EuiFlexGroup>
              <EuiFlexItem>
                <EuiFormRow label="詩歌ID">
                  <EuiFieldText value={extractIdNumber(selectedSong.id)} readOnly />
                </EuiFormRow>

                <EuiFormRow label="歌名">
                  <EuiFieldText
                    value={selectedSong.title}
                    onChange={(e) => setSelectedSong({ ...selectedSong, title: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="鏈接">
                  <EuiFlexGroup gutterSize="s">
                    <EuiFlexItem>
                      <EuiFieldText
                        value={selectedSong.link}
                        onChange={(e) => setSelectedSong({ ...selectedSong, link: e.target.value })}
                      />
                    </EuiFlexItem>
                    {isNewSong ? <EuiFlexItem grow={false}>
                      <EuiButton
                        iconType="logoYoutube"
                        isLoading={isFetchingYouTubeMetadata}
                        onClick={fillSongFromYouTube}
                      >
                        自動填入
                      </EuiButton>
                    </EuiFlexItem> : <>
                      <EuiFlexItem grow={false}>
                        <EuiButton
                          iconType="popout"
                          href={selectedSong.link || undefined}
                          target="_blank"
                          rel="noreferrer"
                          disabled={!getYouTubeVideoId(selectedSong.link)}
                        >
                          打開連結
                        </EuiButton>
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiButton
                          iconType="refresh"
                          isLoading={isFetchingYouTubeMetadata}
                          disabled={!getYouTubeVideoId(selectedSong.link)}
                          onClick={updateYouTubeViewCount}
                        >
                          更新觀看次數
                        </EuiButton>
                      </EuiFlexItem>
                    </>}
                  </EuiFlexGroup>
                </EuiFormRow>

                <EuiFormRow label="發佈者">
                  <EuiFieldText
                    value={selectedSong.publisher}
                    onChange={(e) => setSelectedSong({ ...selectedSong, publisher: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="歌詞">
                  <EuiTextArea
                    value={selectedSong.description}
                    onChange={(e) => setSelectedSong({ ...selectedSong, description: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="語言">
                  <EuiComboBox
                    options={languageOptions}
                    selectedOptions={selectedSong.language ? [{ label: selectedSong.language }] : []}
                    onChange={(selected) => {
                      const newLanguage = selected.length ? selected[0].label : '';
                      setSelectedSong({ ...selectedSong, language: newLanguage });
                    }}
                    onCreateOption={(newOption) => {
                      setLanguageOptions([...languageOptions, { label: newOption }]);
                      setSelectedSong({ ...selectedSong, language: newOption });
                    }}
                  />
                </EuiFormRow>
              </EuiFlexItem>

              <EuiFlexItem>
              <EuiFormRow label="時長 (hh:mm:ss)">
                <EuiFieldText
                    value={selectedSong.length}
                    onChange={(e) => {
                        const input = e.target.value;  // 获取用户输入的值
                        setSelectedSong({ ...selectedSong, length: input });  // 允许用户自由输入，不进行判断
                        }}
                />
                </EuiFormRow>

                <EuiFormRow label="節奏">
                  <EuiComboBox
                    options={tempoOptions}
                    selectedOptions={selectedSong.tempo ? [{ label: selectedSong.tempo }] : []}
                    onChange={(selected) => {
                      const newTempo = selected.length ? selected[0].label : '';
                      setSelectedSong({ ...selectedSong, tempo: newTempo });
                    }}
                    onCreateOption={(newOption) => {
                      setTempoOptions([...tempoOptions, { label: newOption }]);
                      setSelectedSong({ ...selectedSong, tempo: newOption });
                    }}
                  />
                </EuiFormRow>

                <EuiFormRow label="音調">
                  <EuiFieldText
                    value={selectedSong.key}
                    onChange={(e) => setSelectedSong({ ...selectedSong, key: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="標簽">
                  <EuiFieldText
                    value={Array.isArray(selectedSong.tag) ? selectedSong.tag.join(', ') : selectedSong.tag}
                    onChange={(e) => setSelectedSong({ ...selectedSong, tag: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="官方">
                  <EuiComboBox
                    options={officialOptions}
                    selectedOptions={selectedSong.official ? [{ label: selectedSong.official }] : []}
                    onChange={(selected) => {
                      const newOfficial = selected.length ? selected[0].label : '';
                      setSelectedSong({ ...selectedSong, official: newOfficial });
                    }}
                    onCreateOption={(newOption) => {
                      setOfficialOptions([...officialOptions, { label: newOption }]);
                      setSelectedSong({ ...selectedSong, official: newOption });
                    }}
                  />
                </EuiFormRow>

                <EuiFormRow label="經文">
                  <EuiFieldText
                    value={selectedSong.verselist}
                    onChange={(e) => setSelectedSong({ ...selectedSong, verselist: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="觀看次數">
                  <EuiFieldText
                    value={selectedSong.viewct}
                    onChange={(e) => setSelectedSong({ ...selectedSong, viewct: e.target.value })}
                  />
                </EuiFormRow>

                <EuiFormRow label="上傳日期">
                  {/* <EuiFieldText
                    // value={selectedSong.date}
                    value={selectedSong.date ? dayjs(selectedSong.date).format("YYYY-MM-DD") : ""}
                    onChange={(e) => setSelectedSong({ ...selectedSong, date: e.target.value })}
                  /> */}
                  <EuiDatePicker
                    selected={selectedSong.date ? dayjs(selectedSong.date) : null}
                    onChange={(date) => setSelectedSong({ ...selectedSong, date: date ? date.format("YYYY-MM-DD") : "" })}
                    dateFormat="YYYY-MM-DD"
                  />
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGroup>

            {/* 显示 resultMessage 的位置 */}
            <div style={{ textAlign: 'center' }}>
              <EuiText color="danger" style={{ marginBottom: '10px' }}>{resultMessage}</EuiText>
              <EuiFlexGroup justifyContent="center" gutterSize="s">
                <EuiFlexItem grow={false}>
                  <EuiButton onClick={handleSaveSong}>保存修改</EuiButton>
                </EuiFlexItem>
                
                <EuiFlexItem grow={false}>
                  <EuiButton onClick={handleAddplaylistClick}>加入电台</EuiButton>
                </EuiFlexItem>

                {/* <EuiFlexItem grow={false}>
                  <EuiButton onClick={handleDeleteSongClick}>刪除詩歌</EuiButton>
                </EuiFlexItem> */}
              </EuiFlexGroup>
            </div>
          </EuiPanel>
        ) : (
          <EuiText>请选择一首诗歌以查看详细信息。</EuiText>
        )}
          <EuiFlexItem>
            {showFilterOptions && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "10px",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                }}
              >
                <h3>检查完成！</h3>
                <p>检查了 {checkedNum} 首诗歌, 有 {invalidsongs.length} 首失效，耗时：{progress.elapsedTime} 秒。</p>
                <div>
                  <EuiButton
                    style={{ marginRight: "10px" }}
                    onClick={() =>{
                      setTotalCount(songs.length);  // 更新总条数
                      setShowSongs(songs);}
                    }
                  >
                    显示所有诗歌
                  </EuiButton>
                  <EuiButton
                    style={{ marginRight: "10px" }}
                    onClick={() =>{
                      setTotalCount(checkedsongs.length);  // 更新总条数
                      setShowSongs(checkedsongs);}
                    }
                  >
                    显示本次检查诗歌
                  </EuiButton>
                  {invalidsongs.length > 0 && ( // 如果失效诗歌数量大于 0，则显示按钮
                    <>
                    <EuiButton
                      color="danger"
                      style={{marginRight: '10px' }}
                      onClick={() => {
                        setTotalCount(invalidsongs.length); // 更新总条数
                        setShowSongs(invalidsongs);
                      }}
                    >
                      显示失效诗歌
                    </EuiButton>
                    <EuiButton
                      style={{marginRight: '10px' }}
                      onClick={() => saveAsJson(invalidsongs)}
                    >
                      保存失效詩歌 JSON
                    </EuiButton>
                    <EuiButton
                      style={{marginRight: '10px' }}
                      onClick={() => saveAsExcel(invalidsongs)}
                      isLoading={isExportingExcel}
                      isDisabled={isExportingExcel}
                    >
                      保存失效詩歌 Excel
                    </EuiButton>
                    </>
                    )}
                </div>
              </div>
            )}                              
            {/* 新建诗歌按钮 */}
            <div style={{ textAlign: 'center' }}>
              <EuiButton style={{ width: '100px', marginRight: '10px'  }} onClick={handleNewSong}>新建诗歌</EuiButton>
              <EuiButton style={{ marginRight: '10px' }} iconType="logoYoutube" fill onClick={() => setShowYouTubeImporter(true)}>從 YouTube 頻道匯入</EuiButton>
              <EuiButton style={{ width: '100px', marginRight: '10px'  }} onClick={handleSongCheck}>诗歌檢查</EuiButton>
              <EuiButton
                style={{ width: '120px', marginRight: '10px' }}
                onClick={() => saveAsJson(songs)}
              >
                {TranslateLanguage(localStorage.userLanguage, '保存全部 JSON')}
              </EuiButton>
              <EuiButton
                style={{ width: '120px', marginRight: '10px' }}
                onClick={() => saveAsExcel(songs)}
                isLoading={isExportingExcel}
                isDisabled={isExportingExcel}
              >
                {TranslateLanguage(localStorage.userLanguage, '保存全部 Excel')}
              </EuiButton>
                {/* 遮罩层 */}
                {isChecking && (
                  <div>
                    {/* 半透明遮罩 */}
                    <div
                      style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        // width: "100vw",
                        // height: "100vh",
                        width: '100%',
                        height: '100%',
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 1000,
                      }}
                    ></div>

                    {/* 弹窗 */}
                    <div
                      style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "white",
                        padding: "20px",
                        borderRadius: "8px",
                        textAlign: "center",
                        zIndex: 1100,
                      }}
                    >
                      <h3>正在检查...</h3>
                      <p>
                        已完成批次：{progress.completed}/{progress.total}
                      </p>
                      <p>当前耗时：{progress.elapsedTime} 秒</p>
                    </div>
                  </div>
                )}
            </div>
            <ScriptureExcelUpdater
              songs={songs}
              onSongsUpdated={handleScriptureSongsUpdated}
            />
            <div style={{ textAlign: 'center' }}>
              <h3>YouTube 詩歌檢查範圍</h3>
              <p>每次透過 YouTube API 自動以 50 首為一批檢查。</p>
            <div style={{ marginBottom: "10px" }}>
                <label>
                  起始编号:
                  <input
                    type="number"
                    value={startIndex}
                    onChange={(e) => {
                      const value = Math.min(Math.max(Number(e.target.value), 1), songs.length); 
                      setStartIndex(value);
                    }}
                    min={1}
                    max={songs.length}
                    style={{ marginLeft: "10px", width: "80px" }}
                  />
                </label>
              </div>
              <div style={{ marginBottom: "10px" }}>
                <label>
                  终止编号:
                  <input
                    type="number"
                    value={endIndex}
                    onChange={(e) => {
                      const value = Math.min(Math.max(Number(e.target.value), 1), songs.length); 
                      setEndIndex(value);
                    }}
                    min={1}
                    max={songs.length}
                    style={{ marginLeft: "10px", width: "80px" }}
                  />
                </label>
              </div>
            </div>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
      {/* 渲染 PlaylistModal */}
      {showPlaylistModal && (
        <PlaylistModal
          userId={modalProps.userId}
          songId={modalProps.songId}
          videoURL={modalProps.videoURL}
          title={modalProps.title}
          type={modalProps.type}
          engineId={modalProps.engineId}
          label={modalProps.label}
          playlisttype={modalProps.playlisttype}
          closeModal={() => setShowModal(false)}
        />
      )}
      {showYouTubeImporter && (
        <YouTubeSongImporter
          existingSongs={songs}
          onClose={() => setShowYouTubeImporter(false)}
          onImport={handleYouTubeImport}
        />
      )}
    </EuiFlexGroup>
  );
};

export default SongManagement;
